import { useState } from "react";
import { MapPin, Loader2, Check, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "@/components/motion/Reveal";
import { joinWaitlist } from "@/lib/api";

// "Request your city" — out-of-market demand capture. Every submission lands
// on the waitlist with source=city-request and a free-text city, which feeds
// the admin City Demand tab (where the founder picks the next market).
export default function RequestCity() {
  const [city, setCity] = useState("");
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const cleanCity = city.trim();
    const cleanEmail = email.trim();
    if (!cleanCity || !cleanEmail) return;
    setSending(true);
    try {
      await joinWaitlist({
        email: cleanEmail,
        city: cleanCity.toLowerCase(),
        source: "city-request",
      });
      setSent(true);
      toast.success(`We'll email you when GateMate lights up ${cleanCity}.`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && /already/i.test(detail)) {
        setSent(true);
        toast.success("You're already on the list — we have your city.");
      } else {
        toast.error("Something went wrong — try again.");
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="relative py-16 md:py-20 overflow-hidden" data-testid="city-request-section">
      <div className="container-x relative z-10">
        <Reveal>
          <div className="glass glass-cyan rounded-3xl p-8 md:p-12 max-w-3xl mx-auto text-center">
            <div className="mx-auto w-12 h-12 rounded-2xl bg-neon-cyan/15 text-neon-cyan grid place-items-center mb-5">
              <MapPin className="w-5 h-5" />
            </div>
            <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight leading-tight">
              Where should GateMate <span className="text-gradient">go next</span>?
            </h2>
            <p className="mt-4 text-zinc-400 leading-relaxed max-w-xl mx-auto">
              The app works anywhere tonight — venue partnerships start in
              Dallas–Fort Worth. Tell us your city and we'll bring partner
              venues there as demand grows.
            </p>

            {sent ? (
              <div className="mt-8 inline-flex items-center gap-2 rounded-full glass px-6 py-3 text-sm text-white" data-testid="city-request-success">
                <Check className="w-4 h-4 text-neon-cyan" />
                You're on the map. We'll email you the moment your city lights up.
              </div>
            ) : (
              <form onSubmit={submit} className="mt-8 flex flex-col sm:flex-row items-stretch gap-3 max-w-xl mx-auto">
                <input
                  type="text"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="Your city"
                  required
                  data-testid="city-request-city-input"
                  className="flex-1 h-12 rounded-full bg-white/5 border border-white/10 text-white placeholder:text-zinc-500 px-5 text-sm focus:outline-none focus:ring-2 focus:ring-neon-cyan/40"
                />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  data-testid="city-request-email-input"
                  className="flex-1 h-12 rounded-full bg-white/5 border border-white/10 text-white placeholder:text-zinc-500 px-5 text-sm focus:outline-none focus:ring-2 focus:ring-neon-cyan/40"
                />
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="city-request-submit"
                  className="inline-flex items-center justify-center gap-1.5 rounded-full h-12 px-6 text-sm font-semibold bg-gradient-to-r from-neon-violet to-neon-cyan text-white btn-glow border-0 disabled:opacity-60"
                >
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Request it <ArrowRight className="w-4 h-4" /></>}
                </button>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
